import { map, pipe, toArray } from '@fxts/core';
import { html, type Html, Page, View } from 'rune-ts';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { MembershipTypeS } from '../../../features/Membership/outbound/share';
import { PaginationData } from '../../../features/Pagination/type';
import { PRODUCT_LIST_SORT } from '../../../features/ProductList/constant';
import {
  generateCreatorMembershipTab,
  MembershipIdType,
} from '../../../features/ProductList/constant/Membership/tabs';
import {
  ProductListApiS,
  ProductListConstantS,
  ProductListTypeS,
} from '../../../features/ProductList/outbound/share';
import { loading, loadingEnd } from '../../../shared/client/global';
import { Alert } from '../../../shared/components/cells/Alert/Alert';
import { PartialByKey } from '../../../shared/type/types';
import { updateUrlParams } from '../../../shared/util';
import { listenDocumentEvent, sendDocumentEvent } from '../../../shared/util/event';
import { preventEscapeErrorJson } from '../../../shared/util/preventEscapeErrorJson';
import { getSharedData } from '../../../shared/util/rune';
import { ButtonCheckbox } from '../../components/atoms/ButtonCheckbox/ButtonCheckbox';
import { Title } from '../../components/atoms/Title/Title';
import { Dropdown, DropdownChangeEvent } from '../../components/cells/Dropdown/Dropdown';
import { FloatingButton_filter } from '../../components/cells/FloatingBox/FloatingButtons/FloatingButton_filter';
import { footer_links } from '../../components/cells/Footer/constant';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import {
  AllMultiSelectButtonGroup,
  AllMultiSelectButtonGroupChangeEvent,
  SelectableButtonData,
} from '../../components/cells/MultiSelectButtonGroup/MultiSelectButtonGroup';
import { Pagination, PaginationChangeEvent } from '../../components/cells/Pagination/Pagination';
import { ProductCardList } from '../../components/cells/ProductCardList/ProductCardList';
import { CreatorShopProductListEmptyScreen } from '../../components/cells/ProductCardList/ProductCardListEmptyScreen';
import { SubHeaderMobile } from '../../components/cells/Header/Global/views/SubHeaderMobile.ts';
import { Tabs } from '../../components/cells/Tabs/Tabs';
import klass from '../CreatorShopMembership/CreatorShopMembership.module.scss';
import { ProductListCount } from '../ProductList/view/ProductListCount';
import {
  CreatorShopMembershipFilter,
  CreatorShopMembershipFilterConfirmEvent,
  CreatorShopMembershipFilterModal,
} from './views/CreatorShopMembershipFilterModal';
import { ProductCardSize } from '@services/MarppleShop/features/ProductList/type';

export type CreatorShopMembershipProductListPageData = {
  pagination: PaginationData;
  layout_options: BaseLayoutOptions;
  type: MembershipIdType;
  membership_project_id: string;
  membership_token?: string;
  domain_name: string;
  plus_domain_name: string;
  membership_products: ProductListTypeS.ProductCardListData & { count: number };
  memberships: (MembershipTypeS.PartialMembership & { selected: boolean; name: string })[];
  sort_options: ProductListTypeS.CreatorProductSortOption[];
  sort: string;
  without_sold_out?: boolean;
  store_id: number;
};

class CreatorShopMembershipProductListBodyView extends View<CreatorShopMembershipProductListPageData> {
  ProductFilterModalView: CreatorShopMembershipFilterModal;
  WithoutSoldOutCheckbox: ButtonCheckbox;
  init_filter: CreatorShopMembershipFilter;

  constructor(data: CreatorShopMembershipProductListPageData) {
    super(data);

    this.init_filter = { memberships: this.data.memberships };

    this.ProductFilterModalView = new CreatorShopMembershipFilterModal(
      {
        is_mobile: this.data.layout_options.is_mobile,
      },
      this.init_filter,
    );

    this.WithoutSoldOutCheckbox = new ButtonCheckbox({
      active: this.data.without_sold_out,
      label: this.data.layout_options.is_mobile
        ? ET('mps2::product::filter::without_sold_out_short')
        : ET('mps2::product::filter::without_sold_out'),
      name: 'without_sold_out',
      is_mobile: this.data.layout_options.is_mobile,
    });
  }

  protected override template(): Html {
    const { is_mobile } = this.data.layout_options;
    const all_selected = !this.data.memberships.find((m) => m.selected);

    const tab_helper = generateCreatorMembershipTab(this.data.domain_name, this.data.type);

    return html`<div class="${klass.page}">
      ${this.ProductFilterModalView}
      ${new Title({
        sub_title: ET('mps2::membership::main::description'),
        size: is_mobile ? 'large' : 'xLarge',
        title: ET('mps2::membership::main::title'),
        custom_klass: klass.title,
      })}
      <div class="${klass.tabs}">
        ${new Tabs({
          tabs: pipe(
            tab_helper.getSubTabsById('membership'),
            map((tab) => ({ ...tab, klass: klass.tab })),
            toArray,
          ),
          current_tab_id: 'products',
        })}
      </div>
      <div class="${klass.body}">
        <div class="${klass.product_section}">
          ${this.data.memberships.length !== 0
            ? html` ${is_mobile
                  ? ''
                  : new AllMultiSelectButtonGroup(this.data.memberships, {
                      all: {
                        id: 'all',
                        name: ET('mps2::product::all'),
                        selected: all_selected,
                      },
                    })}
                <div class="${klass.sort_wrapper}">
                  <div class="${klass.sort_container}">
                    ${new ProductListCount({ count: this.data.membership_products.count, is_mobile })}
                    <div class="${klass.right_panel}">
                      ${this.WithoutSoldOutCheckbox}
                      ${new Dropdown(
                        {
                          options: this.data.sort_options,
                          selected_option_key: this.data.sort ?? this.data.sort_options[0]?.key,
                        },
                        {
                          horizontal: 'right',
                          klass: klass.sort_container,
                          without_container: is_mobile,
                          fit_width_to_longest_name: true,
                          text_align: is_mobile ? 'right' : 'left',
                        },
                      )}
                    </div>
                  </div>
                </div>`
            : ''}
          ${new ProductCardList(this.data.membership_products, {
            grid: is_mobile
              ? {
                  enabled: true,
                  columns: 2,
                  column_gap: 16,
                  row_gap: 24,
                }
              : {
                  enabled: true,
                  columns: 5,
                  column_gap: 16,
                  row_gap: 32,
                },
            resize_width: 600,
            size: is_mobile ? ProductCardSize.small : ProductCardSize.large,
          }).setEmptyListView(new CreatorShopProductListEmptyScreen())}
          ${new Pagination(this.data.pagination, { klass: klass.pagination })}
        </div>
      </div>
    </div>`;
  }

  protected override onRender() {
    SubHeaderMobile.handleChangeMenuOutside();

    this.delegate(PaginationChangeEvent, Pagination, async (e) => {
      return this._changeMembershipProductList({ offset: (e.detail.page ?? 1) - 1 });
    });

    // "품절 상품 제외" 체크박스 이벤트
    this.delegate('change', ButtonCheckbox, () => {
      return this._changeMembershipProductList({ offset: 0 });
    });

    // 드롭박스 정렬 변경 이벤트 시 상품 변경
    this.delegate(DropdownChangeEvent<keyof typeof PRODUCT_LIST_SORT>, Dropdown, async () => {
      return this._changeMembershipProductList({ offset: 0 });
    });

    // 멀티 박스의 멤버십 필터 변경 이벤트 시 상품 변경
    this.delegate(AllMultiSelectButtonGroupChangeEvent, AllMultiSelectButtonGroup, async (e) => {
      return this._selectButtonHandler(e.detail.selected);
    });

    // 상품 필터 모달에서 값 변경 시 이벤트
    this.delegate(CreatorShopMembershipFilterConfirmEvent, CreatorShopMembershipFilterModal, async (e) => {
      this.ProductFilterModalView.updateFilter(e.detail);
      sendDocumentEvent('floating_bot_button_filter_active', {
        active: this.ProductFilterModalView.FilterBody.isValueSelected(),
      });
      return this._selectButtonHandler(e.detail.memberships.filter((m) => m.selected));
    });

    // floating box 내의 filter 버튼 클릭 시, 상품필터 open
    listenDocumentEvent('floating_box_button_click', (e) => {
      if (e.detail.name == FloatingButton_filter.toString()) {
        this.ProductFilterModalView.open();
      }
    });
  }

  private _updateUrlParams(
    option: PartialByKey<MembershipTypeS.CreatorShopMembershipProductListRetrieveOption, 'sort'> & {
      membership_ids?: string;
    },
  ) {
    const { offset, sort, membership_ids, without_sold_out } = option;

    history.replaceState(
      null,
      '',
      updateUrlParams(location.href, {
        sort,
        without_sold_out,
        membership_ids: membership_ids ?? undefined,
        ...(offset ? { page: offset + 1 } : {}),
      }),
    );
  }

  private _selectButtonHandler(selected: SelectableButtonData<string | number>[]) {
    const selected_membership_ids = selected.map((membership) => membership.id);

    this.data.memberships = this.data.memberships.map((m) => {
      return { ...m, selected: selected_membership_ids.includes(m.id) };
    });

    return this._changeMembershipProductList({
      offset: 0,
      membership_ids: selected_membership_ids.length ? selected_membership_ids.join(',') : undefined,
    });
  }

  private async _changeMembershipProductList(
    option: PartialByKey<MembershipTypeS.CreatorShopMembershipProductListRetrieveOption, 'sort'> & {
      membership_ids?: string;
    },
  ) {
    const sort = (this.subView(Dropdown)?.data.selected_option_key ??
      ProductListConstantS.CREATOR_SHOP_SORT.featured) as ProductListTypeS.CreatorShopProductListSort;

    const fetch_option = {
      ...option,
      sort,
      without_sold_out: this.WithoutSoldOutCheckbox.isChecked().toString() as 'true' | 'false',
      store_id: this.data.store_id,
    };

    try {
      loading();
      const membership_product_data = await ProductListApiS.getMembershipProductListApi(fetch_option);
      loadingEnd();
      this.subView(ProductCardList)?.setProductsData(membership_product_data.products).redraw();
      this.subView(ProductListCount)?.setCount(membership_product_data.count).redraw();
      this.data.membership_products = membership_product_data;
      this._updateUrlParams(fetch_option);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      loadingEnd();
      return Alert.open(
        preventEscapeErrorJson({
          title: ET('mps2::common::error_title'),
          message: ET('mps2::common::error_message'),
        }),
      );
    }
  }

  public getSelectedMemberships() {
    return this.data.memberships.filter((m) => m.selected);
  }
}

export class CreatorShopMembershipProductListPage extends Page<CreatorShopMembershipProductListPageData> {
  protected override template(): Html {
    const sharedData = getSharedData(this);
    const { layout_options } = this.data;

    const body = new CreatorShopMembershipProductListBodyView(this.data);

    return html`
      <div>
        ${new LayoutView(
          {
            body,
            footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
          },
          {
            full_width: true,
            has_bottom_tab_bar: !sharedData?.webviewapp,
            selected_bottom_tab_idx: -1,
            floating_box:
              layout_options.is_mobile && this.data.memberships.length
                ? {
                    filter: {
                      active: !!body.getSelectedMemberships().length,
                    },
                    up: true,
                  }
                : undefined,
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}
